import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render-time errors in any subtree (a malformed match payload, a
 * player crash, etc.) so one broken widget can never blank the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production, forward to your monitoring service here.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleRetry = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      return (
        <div className="glass rounded-2xl p-8 flex flex-col items-center text-center gap-3 animate-fade-in">
          <div className="w-10 h-10 rounded-full bg-live/10 flex items-center justify-center text-live text-lg">
            !
          </div>
          <h3 className="font-display font-semibold text-ink-100">
            {this.props.fallbackTitle ?? "Something didn't load"}
          </h3>
          <p className="text-sm text-ink-500 max-w-sm">
            {this.state.error?.message ?? "An unexpected error occurred while rendering this section."}
          </p>
          <button
            onClick={this.handleRetry}
            className="mt-2 px-4 py-2 rounded-lg bg-emerald/10 text-emerald text-sm font-medium hover:bg-emerald/20 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
